/**
 * ImageDatapointPanel — "see images as data" for one image tag.
 *
 * Lists a thing's captured photos for a metric (latest large + a history strip),
 * opens any in a lightbox, and — when canCapture — lets the user add a photo from the
 * web (file/camera → client compress per the tag's option → upload). Reused by the
 * equipment "Images" panel and the dashboard image widget.
 */

import { useRef, useState, type ChangeEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Camera, Image as ImageIcon, Loader2 } from "lucide-react";
import { Button } from "@/lib/ui/button";
import { useToast } from "@/lib/ui/toast";
import { backendErrorMessage, images, type ImageDatapoint } from "@/lib/api";
import { prepareImageForUpload } from "@/lib/images/compress";
import { ImageThumb } from "./ImageThumb";
import { ImageLightbox } from "./ImageLightbox";

export function ImageDatapointPanel({
  thingId,
  metric,
  label,
  canCapture = false,
  compress = true,
  maxDimension,
  subjectKind,
  subjectId,
}: {
  thingId: string;
  metric: string;
  label?: string;
  canCapture?: boolean;
  compress?: boolean;
  maxDimension?: number;
  subjectKind?: string;
  subjectId?: string;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<ImageDatapoint | null>(null);

  const key = ["thing-images", thingId, metric];
  const listQ = useQuery({
    queryKey: key,
    queryFn: () => images.list(thingId, { metric, limit: 24 }),
    enabled: !!thingId && !!metric,
    staleTime: 15_000,
  });
  const items = listQ.data?.items ?? [];
  const latest = items[0];
  const rest = items.slice(1);

  const uploadMut = useMutation({
    mutationFn: async (file: File) => {
      const prepared = await prepareImageForUpload(file, { compress, maxDimension });
      return images.upload(thingId, {
        file: prepared.blob,
        metric,
        compressed: prepared.compressed,
        width: prepared.width,
        height: prepared.height,
        subjectKind,
        subjectId,
        filename: file.name,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key });
      toast.push("ok", "Photo added.");
    },
    onError: (err) =>
      toast.push("error", backendErrorMessage(err) || "Couldn't add the photo."),
  });

  const onPick = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadMut.mutate(file);
    e.target.value = "";
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <ImageIcon className="size-4 shrink-0 text-on-surface-muted" />
          <span className="truncate text-sm font-medium text-on-surface">
            {label ?? metric}
          </span>
          <span className="shrink-0 text-xs text-on-surface-muted">
            {items.length} photo{items.length === 1 ? "" : "s"}
          </span>
        </div>
        {canCapture && (
          <>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={onPick}
            />
            <Button
              size="sm"
              variant="secondary"
              onClick={() => fileRef.current?.click()}
              disabled={uploadMut.isPending}
            >
              {uploadMut.isPending ? (
                <Loader2 className="mr-1.5 size-4 animate-spin" />
              ) : (
                <Camera className="mr-1.5 size-4" />
              )}
              Add photo
            </Button>
          </>
        )}
      </div>

      {listQ.isLoading ? (
        <div className="h-40 animate-pulse rounded-md bg-surface-muted" />
      ) : items.length === 0 ? (
        <div className="rounded-md border border-dashed border-border px-3 py-6 text-center text-xs text-on-surface-muted">
          No photos yet{canCapture ? " — add one above" : ""}.
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => setPreview(latest)}
            className="overflow-hidden rounded-md border border-border"
            aria-label="Open latest photo"
          >
            <ImageThumb
              imageId={latest.image_id}
              alt={metric}
              className="h-48 w-full object-cover"
            />
          </button>
          <div className="flex items-center gap-1 text-[11px] text-on-surface-muted">
            <span>Latest · {new Date(latest.captured_at).toLocaleString()}</span>
            {!latest.compressed && <span>· full quality</span>}
          </div>
          {rest.length > 0 && (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {rest.map((img) => (
                <button
                  key={img.image_id}
                  type="button"
                  onClick={() => setPreview(img)}
                  className="shrink-0 overflow-hidden rounded border border-border"
                  aria-label={`Open photo from ${new Date(img.captured_at).toLocaleString()}`}
                >
                  <ImageThumb imageId={img.image_id} alt={metric} className="size-16 object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {preview && <ImageLightbox image={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}
